/**
 * THE DAILY — client.
 *
 * This file used to talk to Firestore directly: it created today's puzzle doc
 * from the browser, wrote the player's answers into
 * `dailyChallenges/{date}/submissions/{uid}`, and read three counters off the
 * puzzle doc to display "12 submitted · 4 in progress".
 *
 * Every part of that was wrong in the same way — the client was the authority.
 * Nothing ever scored those answers, so the global board (`dailyBoards`) had
 * zero rows after thirty-one days of puzzles, and the counters were incremented
 * by nobody. What the owner was looking at on this screen was not a leaderboard
 * with no players. It was a leaderboard that was never plugged in.
 *
 * So the client no longer decides anything about the Daily. It POSTs four
 * strings and renders what comes back:
 *
 *   GET  /api/daily              today's puzzle + my sheet + the board
 *   POST /api/daily/submit       { answers } — no date, no score
 *   GET  /api/daily/:date/grid   the share grid (statuses; never words)
 *
 * `todayLetter()` stays, and stays exactly as it was: HomeScreen imports it to
 * show the letter on its card, and it must agree with what /daily serves. It is
 * a pure function of the UTC date, and `backend/src/share/daily.js` holds the
 * identical table — there is a test that reads THIS FILE and asserts the two
 * still match, because they drifted once already.
 */

import { firebaseAuth } from './firebase';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '';

/* The playable letters. Q, U, V, X, Y, Z are excluded — a family game
   should never open with "name an animal starting with X". */
const LETTERS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'R', 'S', 'T', 'W'];

/** Today's challenge id, in UTC. The same day for everyone on earth. */
export function todayKey(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Deterministic letter for a given date (YYYY-MM-DD, UTC).
 *
 * THIS IS THE ONLY CLIENT IMPLEMENTATION. HomeScreen used to compute the daily
 * letter itself — day-of-year mod 26, in LOCAL time — so the letter on the home
 * card and the letter you actually got on /daily routinely disagreed. Anything
 * that needs to display today's letter imports `todayLetter()`.
 *
 * The server does not trust this; it computes the letter itself from the date.
 * This copy exists so the home card can render without a round trip.
 */
export function letterForDate(date: string): string {
  const d = new Date(date + 'T00:00:00Z');
  return LETTERS[(d.getUTCDate() + d.getUTCMonth()) % LETTERS.length];
}

/** Today's letter — the same one `/daily` will serve. */
export function todayLetter(): string {
  return letterForDate(todayKey());
}

/* ------------------------------------------------------------------- types */

/** One square. A status and a number — and, deliberately, no word. */
export interface DailyCell {
  category: string;
  status: 'unique' | 'duplicate' | 'blank' | 'invalid';
  points: number;
}

export interface DailyBoardRow {
  uid: string;
  displayName: string;
  avatar: string;
  score: number;
  rank: number;
  medal?: 'gold' | 'silver' | 'bronze' | null;
  submittedAtMs?: number;
}

/** Where I stand. A band rather than a number below the top 100 — deliberate. */
export interface DailyStanding {
  played: boolean;
  score?: number;
  total?: number;
  rank?: number | null;
  band?: string | null;
  percentile?: number | null;
  delta?: number | null;
  topHundred?: boolean;
}

export interface DailyMe {
  played: boolean;
  /** My own words, back to me. The board rows carry no text at all. */
  answers: Record<string, string> | null;
  isGuest: boolean;
  onBoard: boolean;
  score: number | null;
  cells: DailyCell[] | null;
  standing: DailyStanding | null;
}

export interface DailyToday {
  date: string;
  letter: string;
  categories: string[];
  opensAtMs: number;
  closesAtMs: number;
  /** Real. The number of people your answers were scored against. */
  playerCount: number;
  me: DailyMe;
  board: DailyBoardRow[];
}

export interface DailySubmitResult {
  ok: boolean;
  error?: string;
  guest?: boolean;
  onBoard?: boolean;
  entry?: { uid: string; score: number; cells: DailyCell[] } | null;
  playedToday?: number;
}

/* -------------------------------------------------------------------- wire */

async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  if (!API_BASE) throw new Error('no-api-base');
  const token = await firebaseAuth.getCurrentUserToken();
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init.headers || {}),
    },
  });
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const err = new Error(body?.error || `http-${res.status}`) as Error & { status: number; body: unknown };
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return body as T;
}

/** Today's puzzle, my sheet, and the board. One call, because it is one screen. */
export async function fetchToday(): Promise<DailyToday | null> {
  try {
    return await api<DailyToday>('/daily');
  } catch (e) {
    console.warn('[daily] fetchToday failed', (e as Error)?.message || e);
    return null;
  }
}

/**
 * Play today. Answers, and nothing else — the server picks the day and the
 * referee picks the score.
 *
 * A 409 is not a failure the player can do anything about: they already played.
 * We hand it back as a result rather than throwing, so the screen can show them
 * their sheet instead of an error.
 */
export async function submitToday(answers: Record<string, string>): Promise<DailySubmitResult> {
  try {
    return await api<DailySubmitResult>('/daily/submit', {
      method: 'POST',
      body: JSON.stringify({ answers }),
    });
  } catch (e) {
    const err = e as Error & { status?: number; body?: DailySubmitResult };
    if (err.status === 409) return { ok: false, error: 'already-submitted' };
    if (err.status === 429) return { ok: false, error: 'rate-limited' };
    return { ok: false, error: err.message || 'failed' };
  }
}
